<template>
  <div>
    <v-layout row v-if="errors">
      <v-flex>
        <Alert @dismissed="onDismissed" :text="errors"></Alert>
      </v-flex>
    </v-layout>
    <v-card class="pa-5" flat>
      <div>
        <PageTitleWithSearch
          :title="'Requisitions'"
          :btnName="'Add New Requisition'"
          :btnColor="'#00B3FF'"
          :btnRoute="'/Requisition/create'"
          :btnPermission="'requisition.create'"
          :btnEnable="true"
          :loading="loading"
          :search="search"
          @searchText="searchChange($event)"
        />
      </div>
      <div>
        <DataTableWithPagination
          :headers="headers"
          :items="requisitions"
          :loading="loading"
          :page="page"
          :total="total"
          @pageChange="page = $event"
        >
          <template #row="{ item }">
            <tr
              @click="hasPermission('requisition.view') && goToItemList(item)"
            >
              <td>{{ item.code }}</td>
              <td>{{ item.branch.code }}</td>
              <td>{{ formatDate(item.expected_delivery_date, dateFormat) }}</td>
              <td>{{ item.comment }}</td>
              <td v-if="item.status">
                <v-chip
                  class="py-3"
                  x-small
                  dark
                  :color="getStatusColor(item.status)"
                >
                  {{ stringFormatting(item.status) }}
                </v-chip>
              </td>
              <td>
                <span
                  v-if="item.status && item.status.toLowerCase() === 'pending'"
                >
                  <v-progress-circular
                    :value="getStockPercentValue(item)"
                    :size="32"
                    :width="4"
                    color="#32CD32"
                    class="mr-2"
                  />
                  {{ getStockPercentValue(item) }}%
                </span>
              </td>
              <td class="text-left">
                <v-btn
                  v-if="
                    item.status &&
                      item.status.toLowerCase() === 'pending' &&
                      hasPermission('requisition.edit')
                  "
                  class="ma-1"
                  color="#FFE0B2"
                  :to="'/requisition/edit/' + item.id"
                  fab
                  x-small
                  style="box-shadow: none;"
                >
                  <v-icon small color="#FF9800">mdi-pencil</v-icon>
                </v-btn>
                <v-btn
                  v-else
                  class="ma-1"
                  disabled
                  fab
                  x-small
                  style="box-shadow: none;"
                  ><v-icon small color="#FF9800">mdi-pencil</v-icon></v-btn
                >
                <v-btn
                  v-if="
                    item.status &&
                      item.status.toLowerCase() === 'pending' &&
                      hasPermission('requisition.delete')
                  "
                  class="my-1"
                  color="#FFCDD2"
                  @click.stop="dropMemberConfirm(item.id)"
                  fab
                  x-small
                  style="box-shadow: none;"
                >
                  <v-icon dark small color="#F44336">mdi-delete</v-icon>
                </v-btn>
                <v-btn
                  v-else
                  class="my-1"
                  disabled
                  fab
                  x-small
                  style="box-shadow: none;"
                >
                  <v-icon small color="#FF9800">mdi-delete</v-icon>
                </v-btn>
              </td>
            </tr>
          </template>
        </DataTableWithPagination>
      </div>
      <ConfirmModal
        :text="'Do you want to drop this Requisition?'"
        :title="'Drop Requisition'"
        :color="'red'"
        :confirmOpen="confirmOpen"
        @cancelConfirm="cancelConfirm()"
        @confirm="confirm()"
      />
      <Snackbars
        :text="successMsg"
        :snackbar="snackbar"
        :color="'success'"
        @closeSnackbar="closeSnackbar"
      />
      <v-dialog v-model="storeDialog" max-width="600px">
        <v-card>
          <v-card-text>
            <v-row>
              <v-col cols="12">
                <div class="mt-5 mb-5" v-if="branches.length">
                  <strong>Branch:</strong> {{ branches[0].name }}
                </div>
                <div style="max-width: 400px;">
                  <v-autocomplete
                    v-model="selectedStores"
                    :items="stores"
                    item-text="store_name"
                    item-value="id"
                    multiple
                    label="Select Stores"
                  />
                </div>
              </v-col>
            </v-row>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn text @click="storeDialog = false">Cancel</v-btn>
            <v-btn color="primary" @click="confirmStoreSelection">OK</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-card>
  </div>
</template>

<style scoped>
.router-link {
  text-decoration: none;
}
.dark-row {
  background-color: #fafafa;
  color: rgb(0, 0, 0);
}
.light-row {
  background-color: #ffffff;
}
.custom-row {
  height: 40px;
}
</style>

<script>
import { RequisitionService } from "@/services/requisition-service.js";
import { StockTransferService } from "@/services/stockTransfer-service.js";
import PageTitleWithSearch from "@/components/shared/PageTitleWithSearch";
import Snackbars from "@/components/shared/Snackbars";
import ConfirmModal from "@/components/shared/ConfirmModal";
import { checkError } from "@/config/errors.js";
import DataTableWithPagination from "@/components/shared/DataTableWithPagination.vue";
import { createNamespacedHelpers } from "vuex";
const { mapGetters } = createNamespacedHelpers("auth");
const { mapGetters: mapSettingsGetters } = createNamespacedHelpers("settings");
import Alert from "@/components/shared/Alert.vue";
import { useDateFormatter } from "../../composables/useDateFormatter.js";

export default {
  components: {
    PageTitleWithSearch,
    Snackbars,
    ConfirmModal,
    DataTableWithPagination,
    Alert,
  },
  data() {
    return {
      page: 1,
      total: 0,
      totalRequisitions: 0,
      dropId: null,
      confirmOpen: false,
      loading: true,
      errors: null,
      requisitions: [],
      snackbar: false,
      successMsg: "",
      search: "",
      branches: [],
      mainBranch: null,
      stores: [],
      storeDialog: false,
      selectedStores: [],
      headers: [
        { text: "Code", value: "code" },
        { text: "Branch", value: "branch.code" },
        { text: "Expected Delivery Date", value: "expected_delivery_date" },
        { text: "Comment", value: "comment" },
        { text: "Status", value: "status" },
        { text: "Stock %", value: "stock_percent" },
        { text: "Actions" },
      ],
    };
  },
  created() {
    this.fetchRequisitions();
  },
  setup() {
    const { formatDate } = useDateFormatter();
    return {
      formatDate,
    };
  },
  watch: {
    search() {
      this.fetchRequisitions();
    },
    page() {
      this.fetchRequisitions();
    },
  },

  mounted() {
    this.fetchBranches();
  },

  methods: {
    getStatusColor(status) {
      console.log("Status:", status);
      switch (status) {
        case "completed":
          return "green";
        case "pending":
          return "orange";
        case "cancelled":
          return "red";
        default:
          return "grey";
      }
    },

    async fetchBranches() {
      try {
        const branchesResponse = await StockTransferService.getMainBranchAndMainStores();
        this.branches = branchesResponse.data;
        if (this.branches.length > 0) {
          this.mainBranch = this.branches[0].id;
        }
        console.log("Branches fetched:", this.branches);
        this.stores = this.branches.reduce(
          (acc, branch) => acc.concat(branch.stores || []),
          []
        );
      } catch (error) {
        console.error("Error fetching data:", error);
        let data = error.response.data;
        let errorOutput = checkError.errorFormatting(data);
        this.errors = errorOutput;
      }
    },

    stringFormatting(status) {
      return status.charAt(0).toUpperCase() + status.slice(1);
    },

    async fetchRequisitions(branchId = null, storeIds = []) {
      this.loading = true;
      try {
        const response = await RequisitionService.getRequisitions(
          this.search,
          branchId,
          storeIds,
          this.page
        );
        this.requisitions = response.data.data;
        this.total = response.data.meta.last_page || 0;
        this.totalRequisitions = response.data.meta.total || 0;
      } catch (error) {
        console.error("Error fetching requisitions:", error);
      } finally {
        this.loading = false;
      }
    },

    async deleteRequisition(id) {
      try {
        await RequisitionService.deleteRequisition(id);
        this.successMsg = "Requisition deleted successfully.";
        this.snackbar = true;
        this.fetchRequisitions();
      } catch (error) {
        let data = "Unable to delete requisition.";

        let errorOutput = checkError.errorFormatting(data);
        let errorsArray = [];
        if (Array.isArray(errorOutput)) {
          errorsArray = errorOutput.filter((e) => !!e && e.trim());
          if (errorsArray.length === 0) errorsArray = [data];
        } else if (errorOutput && errorOutput.trim()) {
          errorsArray = [errorOutput];
        } else {
          errorsArray = [data];
        }
        this.errors = errorsArray;
      }
    },

    getStockPercentValue(item) {
      if (!item.items || !item.items.length) return 0;
      const totalQuantity = item.items.reduce(
        (sum, i) => sum + (parseFloat(i.quantity) || 0),
        0
      );
      const totalStock = item.items.reduce((sum, i) => {
        if (
          i.stock_masters_in_branch1 &&
          Array.isArray(i.stock_masters_in_branch1)
        ) {
          return (
            sum +
            i.stock_masters_in_branch1.reduce(
              (acc, stock) =>
                acc + parseFloat(stock.stock_in_hand_quantity || 0),
              0
            )
          );
        }
        return sum;
      }, 0);
      if (totalQuantity === 0) return 0;
      return Math.min(100, ((totalStock / totalQuantity) * 100).toFixed(2));
    },

    goToItemList(item) {
      this.$router.push({
        name: "requisition-details",
        params: { id: item.id },
        query: {
          branchId: this.mainBranch,
          storeIds: this.selectedStores.join(","),
        },
      });
    },

    dropMemberConfirm(id) {
      this.dropId = id;
      this.confirmOpen = true;
    },

    cancelConfirm() {
      this.confirmOpen = false;
    },

    confirm() {
      this.confirmOpen = false;
      this.deleteRequisition(this.dropId);
    },

    closeSnackbar() {
      this.snackbar = false;
    },
    searchChange(value) {
      this.search = value;
    },
    openStoreModalFromHeader() {
      this.storeDialog = true;
    },

    openStoreModal(item) {
      this.selectedRequisition = item;
      this.selectedStores = [];
      this.storeDialog = true;
    },
    confirmStoreSelection() {
      const branchId = this.mainBranch ? this.mainBranch : null;
      const storeIds = this.selectedStores.slice();

      this.fetchRequisitions(branchId, storeIds);
      this.storeDialog = false;
    },

    clearSelectedStores() {
      this.selectedStores = [];
      this.$router.replace({
        query: {
          ...this.$route.query,
          storeIds: "",
        },
      });
      this.fetchRequisition();
    },

    getBranchName(branchId) {
      const branch = this.branches.find((b) => b.id === branchId);
      return branch ? branch.name : "";
    },

    onDismissed() {
      this.errors = null;
    },
  },
  computed: {
    ...mapGetters({
      hasPermission: "hasPermission",
    }),
    ...mapSettingsGetters({
      dateFormat: "dateFormat",
    }),
    storesForSelectedBranch() {
      if (!this.selectedRequisition) return [];
      const branch = this.branches.find(
        (b) => b.id === this.selectedRequisition.branch_id
      );
      return branch && branch.stores ? branch.stores : [];
    },

    allPending() {
      return (
        this.requisitions.length > 0 &&
        this.requisitions.some(
          (req) => req.status && req.status.toLowerCase() === "pending"
        )
      );
    },
  },
};
</script>
